import { Children, useRef, type ReactNode } from "react";
import { HF } from "./homeFeedUi";

/** "home_garden" → "Home Garden"; category ids come straight from the API. */
export function categoryTitle(id: string): string {
  return id
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
}

function Arrow({ dir, onClick }: { dir: "left" | "right"; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === "left" ? "Scroll left" : "Scroll right"}
      className="hidden h-10 w-10 items-center justify-center rounded-full border bg-[var(--hf-surface)] transition hover:border-[var(--hf-violet-border)] hover:bg-[var(--hf-hover)] sm:flex"
      style={{ borderColor: HF.chipBorder, color: HF.text2 }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={dir === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6"} />
      </svg>
    </button>
  );
}

/** One horizontally scrolling section of the home feed: title, View all, cards. */
export default function DealRow({
  title,
  icon,
  meta,
  action,
  lead,
  children,
  testId,
}: {
  title: string;
  icon?: ReactNode;
  meta?: string;
  action?: ReactNode;
  /** Full-width block between the title and the cards (e.g. an empty state). */
  lead?: ReactNode;
  children?: ReactNode;
  testId?: string;
}) {
  const items = Children.toArray(children);
  const scroller = useRef<HTMLDivElement>(null);
  const scrollBy = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: "smooth" });
  };

  return (
    <section aria-label={title} className="flex flex-col gap-3 sm:gap-4" data-testid={testId}>
      <div className="flex items-center gap-3 px-1">
        <h2 className="flex min-w-0 items-center gap-2 text-[18px] font-extrabold tracking-[-0.4px] sm:text-[22px] sm:tracking-[-0.5px]">
          {icon}
          <span className="truncate">{title}</span>
        </h2>
        {meta && <span className="hidden flex-shrink-0 text-[13px] font-medium tabular-nums sm:inline" style={{ color: HF.text3 }}>{meta}</span>}
        <span className="flex-1" />
        {action}
        {items.length > 0 && (
          <>
            <Arrow dir="left" onClick={() => scrollBy(-1)} />
            <Arrow dir="right" onClick={() => scrollBy(1)} />
          </>
        )}
      </div>
      {lead}
      {items.length > 0 && (
      <div
        ref={scroller}
        className="-mx-3 flex snap-x snap-mandatory scroll-px-3 gap-3 overflow-x-auto scroll-smooth px-3 pb-3 pt-0.5 sm:scroll-px-1 [scrollbar-width:none] sm:-mx-1 sm:gap-4 sm:px-1 [&::-webkit-scrollbar]:hidden"
      >
        {items.map((child, index) => (
          <div key={index} className="w-[42vw] max-w-[176px] flex-shrink-0 snap-start sm:w-[204px] sm:max-w-none">
            {child}
          </div>
        ))}
      </div>
      )}
    </section>
  );
}

export function ViewAllButton({ onClick, label = "View all" }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-bold transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--hf-page)] sm:h-10 sm:px-4 sm:text-[13.5px]"
      style={{ background: HF.inkBtn, color: HF.onInkBtn }}
    >
      {label}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
    </button>
  );
}
